// =============================================================================
// src/views/negocios/index.ts — AppModule da Tela 06 (Negocios Setorial).
//
// Proveniencia: markup FIEL de unified.html (linhas 968-1058, section
//   data-section="negocios") + comportamento portado de Sections.negocios
//   (assets/unified-app.js 1505-1586). O markup mora aqui; o comportamento
//   (estado/modos/listeners/insight) mora em negocios-controller.ts.
//
// Ciclo de vida (contrato SectionLifecycleModule): activate(ctx)/deactivate() sao
//   disparados pela SectionManager TS no scroll-spy. init(ctx) cobre o bootstrap.
//   Guardamos ctx.map em activate para deactivate() (sem args) repassar ao controlador.
// =============================================================================

import type { AppContext, MapController } from '../../app/types';
import type { SectionLifecycleModule } from '../../core/section-manager';
import { negociosController } from './negocios-controller';
import { buildProvenanceFooterMarkup } from '../../data/data-provenance';

/** Markup da secao negocios (baseado no unified.html; divergencia consciente 2026-05-31:
 *  numero "06 · " removido do titulo a pedido do usuario). */
const NEGOCIOS_MARKUP = `    <section class="u-section" id="section-negocios" data-section="negocios">
      <div class="u-section-header">
        <div class="u-section-title">Negócios Setorial</div>
        <div class="u-section-subtitle">Mapa econômico por setor, grupo empresarial e doações eleitorais</div>
      </div>
      <div class="u-section-body">
        <!-- View bar -->
        <div class="u-mode-bar">
          <div class="u-mode-tabs">
            <button class="u-mode-tab active" data-mode="clusters"><i class="ti ti-building"></i> Clusters de empresas</button>
            <button class="u-mode-tab" data-mode="heatmap"><i class="ti ti-flame"></i> Heatmap econômico</button>
            <button class="u-mode-tab" data-mode="dominant"><i class="ti ti-target-arrow"></i> Setor dominante</button>
          </div>
        </div>

        <!-- Tabs -->
        <div style="border-bottom:0.5px solid var(--color-border-tertiary);padding:0 16px;display:flex;gap:0">
          <button class="u-tab u-neg-tab active" data-tab="sectors" style="padding:8px 14px;font-size:11px;border:none;background:transparent;border-bottom:2px solid #185FA5;color:#185FA5;font-weight:500;cursor:pointer;font-family:inherit"><i class="ti ti-chart-pie" style="font-size:12px"></i> Setores</button>
          <button class="u-tab u-neg-tab" data-tab="firms" style="padding:8px 14px;font-size:11px;border:none;background:transparent;color:var(--color-text-secondary);cursor:pointer;font-family:inherit"><i class="ti ti-buildings" style="font-size:12px"></i> Grupos</button>
          <button class="u-tab u-neg-tab" data-tab="donations" style="padding:8px 14px;font-size:11px;border:none;background:transparent;color:var(--color-text-secondary);cursor:pointer;font-family:inherit"><i class="ti ti-coin" style="font-size:12px"></i> Doações</button>
        </div>

        <div class="u-neg-panel" data-panel="sectors" style="display:block">
        <div class="u-panel">
          <!-- Sector chips -->
          <div class="u-chips-row">
            <span class="u-sector-chip selected" data-s="all"><span class="sc-dot" style="background:#888780"></span>Todos</span>
            <span class="u-sector-chip selected" data-s="com"><span class="sc-dot" style="background:#BA7517"></span>Comércio</span>
            <span class="u-sector-chip selected" data-s="ind"><span class="sc-dot" style="background:#534AB7"></span>Indústria</span>
            <span class="u-sector-chip selected" data-s="svc"><span class="sc-dot" style="background:#185FA5"></span>Serviços</span>
            <span class="u-sector-chip" data-s="agro"><span class="sc-dot" style="background:#639922"></span>Agro</span>
            <span class="u-sector-chip" data-s="fin"><span class="sc-dot" style="background:#378ADD"></span>Financeiro</span>
          </div>

          <!-- Ranking by revenue -->
          <div>
            <div class="u-panel-section-label">Ranking por receita bruta</div>
            <div class="u-ranking-row">
              <div class="u-rank-pos">1</div>
              <div class="u-rank-name">Comércio varejista<span style="display:block;font-size:10px;font-weight:400;color:var(--color-text-secondary)">12.400 empresas ativas</span></div>
              <div class="u-rank-bar-track"><div class="u-rank-bar-fill" style="width:90%;background:#185FA5"></div></div>
              <div class="u-rank-value" style="color:#185FA5">R$ 4,2 bi</div>
            </div>
            <div class="u-ranking-row">
              <div class="u-rank-pos">2</div>
              <div class="u-rank-name">Indústria manufatureira<span style="display:block;font-size:10px;font-weight:400;color:var(--color-text-secondary)">3.800 empresas ativas</span></div>
              <div class="u-rank-bar-track"><div class="u-rank-bar-fill" style="width:70%;background:#185FA5"></div></div>
              <div class="u-rank-value" style="color:#185FA5">R$ 3,1 bi</div>
            </div>
            <div class="u-ranking-row">
              <div class="u-rank-pos">3</div>
              <div class="u-rank-name">Serviços especializados<span style="display:block;font-size:10px;font-weight:400;color:var(--color-text-secondary)">8.200 empresas ativas</span></div>
              <div class="u-rank-bar-track"><div class="u-rank-bar-fill" style="width:55%;background:#185FA5"></div></div>
              <div class="u-rank-value" style="color:#185FA5">R$ 2,4 bi</div>
            </div>
            <div class="u-ranking-row">
              <div class="u-rank-pos">4</div>
              <div class="u-rank-name">Construção civil<span style="display:block;font-size:10px;font-weight:400;color:var(--color-text-secondary)">2.100 empresas ativas</span></div>
              <div class="u-rank-bar-track"><div class="u-rank-bar-fill" style="width:40%;background:#185FA5"></div></div>
              <div class="u-rank-value" style="color:#185FA5">R$ 1,7 bi</div>
            </div>
            <div class="u-ranking-row">
              <div class="u-rank-pos">5</div>
              <div class="u-rank-name">Financeiro e seguros<span style="display:block;font-size:10px;font-weight:400;color:var(--color-text-secondary)">980 empresas ativas</span></div>
              <div class="u-rank-bar-track"><div class="u-rank-bar-fill" style="width:25%;background:#185FA5"></div></div>
              <div class="u-rank-value" style="color:#185FA5">R$ 1,1 bi</div>
            </div>
          </div>

          <button class="u-cta-btn" onclick="sendPrompt('Analisar correlação entre setores econômicos e voto')"><i class="ti ti-link"></i> Cruzar com dados eleitorais</button>
        </div>
        </div>

        <div class="u-neg-panel" data-panel="firms" style="display:none;padding:0 16px">
          <div class="u-panel-section-label">Top grupos econômicos · Manaus</div>
          <div class="u-firm-row"><div class="u-firm-avatar" style="background:#534AB7"><i class="ti ti-building-factory-2"></i></div><div class="u-firm-info"><div class="u-firm-name">Grupo Indústria Norte</div><div class="u-firm-sub">eletroeletr. · R$ 4,2 bi · 12 mil func.</div></div><span class="u-firm-tilt local">apoia Avante</span></div>
          <div class="u-firm-row"><div class="u-firm-avatar" style="background:#BA7517"><i class="ti ti-building-store"></i></div><div class="u-firm-info"><div class="u-firm-name">Bemar Comercial</div><div class="u-firm-sub">varejo regional · R$ 1,8 bi · 4,2 mil func.</div></div><span class="u-firm-tilt right">apoia PL</span></div>
          <div class="u-firm-row"><div class="u-firm-avatar" style="background:#5F5E5A"><i class="ti ti-building-skyscraper"></i></div><div class="u-firm-info"><div class="u-firm-name">Construtora Amazonas</div><div class="u-firm-sub">incorporadora · R$ 980 mi</div></div><span class="u-firm-tilt right">apoia União</span></div>
          <div class="u-firm-row"><div class="u-firm-avatar" style="background:#D85A30"><i class="ti ti-bolt"></i></div><div class="u-firm-info"><div class="u-firm-name">Energia Cidade</div><div class="u-firm-sub">concessionária · R$ 780 mi</div></div><span class="u-firm-tilt center">neutro</span></div>
        </div>

        <div class="u-neg-panel" data-panel="donations" style="display:none;padding:0 16px">
          <div class="u-panel-section-label">Doações declaradas · TSE 2024</div>
          <div class="u-donation-row"><div><div class="u-don-from">Indústria Norte</div><div class="u-don-to">→ D. Almeida (Avante)</div><div class="u-don-year">jul/2024 · campanha 1º turno</div></div><div class="u-don-amount">R$ 1,2 mi</div></div>
          <div class="u-donation-row"><div><div class="u-don-from">Bemar Comercial</div><div class="u-don-to">→ A. Neto (PL)</div><div class="u-don-year">ago/2024</div></div><div class="u-don-amount">R$ 480 mil</div></div>
          <div class="u-donation-row"><div><div class="u-don-from">Construtora Amazonas</div><div class="u-don-to">→ 5 cands. União</div><div class="u-don-year">jun-set/2024</div></div><div class="u-don-amount">R$ 320 mil</div></div>
          <div class="u-donation-row"><div><div class="u-don-from">Indústria Norte</div><div class="u-don-to">→ Diretório Avante AM</div><div class="u-don-year">2024 · partidária</div></div><div class="u-don-amount">R$ 280 mil</div></div>
        </div>
      </div>
      ${buildProvenanceFooterMarkup('negocios')}
    </section>`;

/** Ultimo mapa visto em activate, para deactivate() (sem args) repassar ao controlador. */
let negociosActiveMap: MapController | null = null;

/**
 * AppModule da secao Negocios com hooks de ciclo de vida (porte de Sections.negocios).
 * render() emite o markup; activate/deactivate sao chamados pela SectionManager no
 * scroll-spy; init(ctx) cobre o bootstrap inicial (ativa se o mapa ja estiver pronto).
 */
export const negociosView: SectionLifecycleModule = {
  id: 'negocios',
  render(): string {
    return NEGOCIOS_MARKUP;
  },
  // Ativacao dirigida SO pelo SectionManager (scroll-spy). init NAO ativa: senao a
  // secao aplica seus efeitos no mapa compartilhado ja no boot, fora de vista. 2026-05-31.
  init(): void {},
  activate(ctx: AppContext): void {
    if (!ctx.map) return;
    negociosActiveMap = ctx.map;
    negociosController.activate(ctx.map);
  },
  deactivate(): void {
    if (negociosActiveMap) negociosController.deactivate(negociosActiveMap);
  },
};

export default negociosView;
